import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import {
    Box,
    Button,
    FormControl,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useApi } from '../../../api';
import { useAlertMessage } from '../../providers/alert-provider';
import { ImageSelector, AuthenticatedImage } from '../shared';
import { Scanner } from '@yudiel/react-qr-scanner';

type Params = Record<'boxId', string>;

export const EditBox: React.FC = () => {
    const params = useParams<Params>();
    const navigate = useNavigate();
    const alert = useAlertMessage();
    const { Api } = useApi();
    const [box, setBox] = useState<Box | null>(null);
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [imageMetadataId, setImageMetadataId] = useState<string | undefined>(undefined);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showImageSelector, setShowImageSelector] = useState(false);
    const [showQrScanner, setShowQrScanner] = useState(false);

    useEffect(() => {
        const boxId = params.boxId;
        if (boxId) {
            Api.Box.getBox(boxId)
                .then(({ data }) => {
                    setBox(data);
                    setCode(data.code);
                    setName(data.name);
                    setDescription(data.description || '');
                    setImageUrl(data.imageUrl || '');
                    setImageMetadataId(data.imageMetadataId);
                })
                .catch((error) => alert.addError(error));
        }
    }, [params]);

    const handleQrScan = (data: unknown) => {
        if (data && Array.isArray(data) && data.length > 0) {
            const result = data[0];
            if (result && typeof result === 'object' && 'rawValue' in result) {
                setCode(String(result.rawValue));
                setShowQrScanner(false);
            }
        }
    };

    const handleQrError = (err: unknown) => {
        console.error('QR Scanner error:', err);
    };

    const handleSave = () => {
        setIsSubmitted(true);
        const isValid = code.trim().length > 0 && name.trim().length > 0;

        if (!saving && isValid && box) {
            setSaving(true);
            const updatedBox: Box = {
                ...box,
                code,
                name,
                description,
                imageUrl,
                imageMetadataId,
            };

            Api.Box.updateBox(box.boxId, updatedBox)
                .then(() => {
                    navigate('..');
                })
                .catch((error) => alert.addError(error))
                .finally(() => setSaving(false));
        }
    };

    const handleImageSelected = (url: string, imageId: string) => {
        if (url === '' && imageId === '') {
            // Clear the image
            setImageUrl('');
            setImageMetadataId(undefined);
        } else {
            setImageUrl(url);
            setImageMetadataId(imageId);
        }
        setShowImageSelector(false);
    };

    if (!box) {
        return null;
    }

    const hasError = (field: string, value: string) =>
        isSubmitted && value.trim().length === 0;

    return (
        <React.Fragment>
            <Box>
                <Paper>
                    <Box
                        sx={{
                            margin: 1,
                            textAlign: "center"
                        }}>
                        <Typography variant="h4">Edit Box</Typography>
                    </Box>
                    <Box sx={{
                        margin: 2
                    }}>
                        <Stack spacing={2}>
                            <FormControl fullWidth>
                                <TextField
                                    variant="standard"
                                    error={hasError('code', code)}
                                    label="Code"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    disabled={saving}
                                    required
                                    helperText={hasError('code', code) ? 'Code is required' : ''}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <IconButton
                                                    onClick={() => setShowQrScanner(!showQrScanner)}
                                                    edge="end"
                                                    disabled={saving}
                                                    aria-label="scan QR code"
                                                    title="Scan QR code"
                                                >
                                                    <QrCodeScannerIcon />
                                                </IconButton>
                                            ),
                                        },
                                    }}
                                />
                            </FormControl>

                            {showQrScanner && (
                                <Box>
                                    <Scanner
                                        onScan={handleQrScan}
                                        onError={handleQrError}
                                        constraints={{
                                            facingMode: 'environment'
                                        }}
                                        styles={{
                                            container: { width: '100%' }
                                        }}
                                    />
                                    <Button
                                        onClick={() => setShowQrScanner(false)}
                                        color="secondary"
                                        fullWidth
                                        sx={{ mt: 1 }}
                                    >
                                        Cancel Scan
                                    </Button>
                                </Box>
                            )}

                            <FormControl fullWidth>
                                <TextField
                                    variant="standard"
                                    error={hasError('name', name)}
                                    label="Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    disabled={saving}
                                    required
                                    helperText={hasError('name', name) ? 'Name is required' : ''}
                                />
                            </FormControl>

                            <FormControl fullWidth>
                                <TextField
                                    variant="standard"
                                    label="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    disabled={saving}
                                    multiline
                                    rows={3}
                                />
                            </FormControl>

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Image
                                </Typography>
                                {imageUrl ? (
                                    <Box
                                        sx={{
                                            mb: 2,
                                            display: "flex",
                                            justifyContent: "center"
                                        }}>
                                        <AuthenticatedImage
                                            src={imageUrl}
                                            alt="Box image"
                                            style={{ maxWidth: 300, maxHeight: 200, objectFit: 'contain' }}
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            mb: 2,
                                            display: "flex",
                                            justifyContent: "center",
                                            py: 4
                                        }}>
                                        <Typography variant="body2" sx={{
                                            color: "text.secondary"
                                        }}>
                                            No image set
                                        </Typography>
                                    </Box>
                                )}
                                <Button
                                    color="primary"
                                    onClick={() => setShowImageSelector(true)}
                                    disabled={saving}
                                >
                                    {imageUrl ? 'Change Image' : 'Select Image'}
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            padding: 2,
                            justifyContent: "right"
                        }}>
                        <Button color="primary" onClick={handleSave} disabled={saving}>
                            Save
                        </Button>
                        <Button color="secondary" component={Link} to="..">
                            Cancel
                        </Button>
                    </Stack>
                </Paper>
            </Box>
            {showImageSelector && (
                <ImageSelector
                    currentImageUrl={imageUrl}
                    onImageSelected={handleImageSelected}
                    onCancel={() => setShowImageSelector(false)}
                />
            )}
        </React.Fragment>
    );
};
